import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import classNames from "classnames";
import { nanoid } from "nanoid";
import { useOnClickOutside } from "usehooks-ts";
import style from "./rule-cell.module.css";
import {
  ElementRule,
  ElementRuleComparator,
  ElementRuleValueType,
  ruleSchema,
} from "shared/datamodel/schemas/element-data-layer";
import { RuleCategorizedDropdown, RuleEditableDropdown, RuleEditableText, RuleIconPicker } from "./rule-editable-value";
import { getElementTypeForId } from "frontend/canvas-designer-new/elements/canvas-elements-utils";
import {
  getPropertiesForElement,
  getPropertyOptions,
  getPropertyTitleForElement,
  getPropertyTypeForElement,
} from "./rule-creator-utils";
import { ColorPicker } from "frontend/canvas-designer-new/elements-toolbar/widgets/color-picker";
import consts from "shared/consts";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { Floater } from "frontend/ui-components/floaters/floater";
import ThreeDotsIcon from "frontend/ui-components/design-system/icons/three-dots-icon";
import { PortalID } from "frontend/modal/portal-types";
import ReactDOM from "react-dom";
import { useDynamicIntegrations } from "frontend/canvas-designer-new/element-dynamic-data/hooks/use-dynamic-integrations";
import { DuplicateRuleIcon } from "frontend/canvas-designer-new/element-dynamic-data/icons/duplicate-rule-icon";
import { DeleteRuleIcon } from "frontend/canvas-designer-new/element-dynamic-data/icons/delete-rule-icon";
import { DisableRuleIcon } from "frontend/canvas-designer-new/element-dynamic-data/icons/disable-rule-icon";
import { EnableRuleIcon } from "frontend/canvas-designer-new/element-dynamic-data/icons/enable-rule-icon";
import { CanvasElement } from "shared/datamodel/schemas";
import tracking from "frontend/tracking";
import { MondayLogo } from "frontend/icons/monday-logo";
import { MissingDataIcon } from "frontend/canvas-designer-new/element-dynamic-data/icons/missing-data-icon";
import { UNSUPPORTED_COLUMNS } from "frontend/canvas-designer-new/element-dynamic-data/consts";

interface ElementDynamicRuleCellProps {
  elementId: string;
  element: CanvasElement;
  rule?: ElementRule;
  newRuleId?: string;
  onChange: (rule: ElementRule) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  isPlaceholder?: boolean;
}

interface RuleEditorSubmenuOption {
  title: string;
  icon: React.ReactNode;
  id: string;
  onClick: () => void;
}

export default function ElementDynamicRuleCell({
  elementId,
  element,
  rule,
  newRuleId,
  onChange,
  onDelete,
  onDuplicate,
  isPlaceholder = false,
}: ElementDynamicRuleCellProps) {
  const { isLoading, fieldIds, integrationsConfig, integrationItemIds, getField } = useDynamicIntegrations(
    elementId,
    element
  );

  const [fieldId, setFieldId] = useState<string | undefined>(rule?.fieldId);
  const [comparator, setComparator] = useState<ElementRuleComparator | undefined>(rule?.comparator);
  const [value, setValue] = useState<string>(rule?.compareValue ?? "");
  const [actionProperty, setActionProperty] = useState<string | undefined>(rule?.action.property);
  const [actionValue, setActionValue] = useState<string | undefined>(rule?.action.value);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const [itemId, setItemId] = useState<string | undefined>(rule?.itemId);
  const isActive = rule?.active ?? true;

  useEffect(() => {
    if (
      itemId !== rule?.itemId ||
      fieldId !== rule?.fieldId ||
      comparator !== rule?.comparator ||
      value !== rule?.compareValue ||
      actionProperty !== rule?.action.property ||
      actionValue !== rule?.action.value
    ) {
      const ruleId = rule?.id ?? newRuleId ?? nanoid(10);
      const newRule = ruleSchema.safeParse({
        id: ruleId,
        itemId,
        fieldId,
        comparator,
        compareValue: value,
        action: { property: actionProperty, value: actionValue },
        active: true,
      });
      if (newRule.success) {
        onChange(newRule.data);
      }

      // Track the first property change on a rule
      const ruleCells = [fieldId, comparator, value, actionProperty, actionValue];
      const isFirstChange = rule === undefined && ruleCells.filter(Boolean).length === 1;
      if (isFirstChange) {
        tracking.trackAnalyticsEvent("data-layer-rule-creation-started", { elementId, ruleId });
      }

      // Track when a rule is finished being created
      const isLastChange = ruleCells.filter(Boolean).length === ruleCells.length;
      if (rule === undefined && isLastChange && newRule.success) {
        tracking.trackAnalyticsEvent("data-layer-rule-creation-completed", { elementId, ruleId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, fieldId, comparator, value, actionProperty, actionValue]);

  const memoizedRule = useDeepEqualMemo(rule);
  useEffect(() => {
    if (memoizedRule) {
      setItemId(memoizedRule.itemId);
      setFieldId(memoizedRule.fieldId);
      setComparator(memoizedRule.comparator);
      setValue(memoizedRule.compareValue);
      setActionProperty(memoizedRule.action.property);
      setActionValue(memoizedRule.action.value);
    }
  }, [memoizedRule]);

  const getFieldTitle = useCallback(
    (id: string): string => {
      if (id === "elementText") {
        return "Element text";
      }
      const field = getField(id);
      return field?.title ?? "Field";
    },
    [getField]
  );

  function getComparatorTitle(comparator: ElementRuleComparator): string {
    switch (comparator) {
      case ElementRuleComparator.eq: {
        return "equals";
      }
      case ElementRuleComparator.neq: {
        return "does not equal";
      }
      case ElementRuleComparator.gt: {
        return "greater than";
      }
      case ElementRuleComparator.gte: {
        return "greater than or equal";
      }
      case ElementRuleComparator.lt: {
        return "less than";
      }
      case ElementRuleComparator.lte: {
        return "less than or equal";
      }
      case ElementRuleComparator.contains: {
        return "contains";
      }
      case ElementRuleComparator.not_contains: {
        return "does not contain";
      }
      default: {
        return "Unknown";
      }
    }
  }

  function shouldRenderIs(comparator?: ElementRuleComparator) {
    switch (comparator) {
      case ElementRuleComparator.gt:
      case ElementRuleComparator.gte:
      case ElementRuleComparator.lt:
      case ElementRuleComparator.lte: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  function shouldRenderTo(comparator?: ElementRuleComparator) {
    switch (comparator) {
      case undefined:
      case ElementRuleComparator.gte:
      case ElementRuleComparator.lte: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  const memoizedFieldIds = useDeepEqualMemo(fieldIds);
  const memoizedIntegrationsConfig = useDeepEqualMemo(integrationsConfig);
  const fieldCategories = [
    { id: "local", title: "SHAPE PROPERTIES" },
    { id: "integrations", title: "ITEM COLUMNS", icon: <MondayLogo size={16} /> },
  ];

  const fieldOptions = useMemo(() => {
    const integrationFieldOptions = Object.entries(memoizedIntegrationsConfig).flatMap(
      ([integrationId, integration]) => {
        if (!integration?.columns) return [];
        return integration.columns.flatMap((column: { id: string; type?: string }) => {
          if (column.type && UNSUPPORTED_COLUMNS.has(column.type)) return [];

          const itemsIds = integrationItemIds[integrationId];
          if (!itemsIds) return [];
          return itemsIds.map((itemId) => ({
            category: "integrations",
            option: `integration:${integrationId}:${itemId}:${column.id}`,
          }));
        });
      }
    );

    const elementTextFieldOption = { category: "local", option: "elementText" };

    // We do not suport custom fields currently but we will in the future
    const customFieldOptions = memoizedFieldIds.map((fieldId) => ({
      category: "local",
      option: fieldId,
    }));

    return [elementTextFieldOption, ...customFieldOptions, ...integrationFieldOptions];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedFieldIds, memoizedIntegrationsConfig]);

  const missingField = useMemo(() => {
    if (isLoading) return false;
    if (!fieldId) return false;
    return !fieldOptions.some((option) => option.option === fieldId);
  }, [isLoading, fieldId, fieldOptions]);

  const actionProperties = useMemo(() => {
    const elementType = getElementTypeForId(elementId);
    return getPropertiesForElement(elementType);
  }, [elementId]);

  const getActionPropertyTitle = useCallback((property: string) => {
    return getPropertyTitleForElement(property);
  }, []);

  const menuOptions = useMemo(() => {
    const options = [];
    if (rule !== undefined) {
      options.push({
        title: isActive ? "Disable" : "Enable",
        icon: isActive ? <DisableRuleIcon /> : <EnableRuleIcon />,
        id: "toggle",
        onClick: () => {
          if (!rule) return;
          onChange({ ...rule, active: !isActive });
          setShowMenu(false);
        },
      });
    }

    if (onDuplicate) {
      options.push({
        title: "Duplicate",
        icon: <DuplicateRuleIcon />,
        id: "duplicate",
        onClick: () => {
          onDuplicate();
          setShowMenu(false);
        },
      } as RuleEditorSubmenuOption);
    }

    if (onDelete) {
      options.push({
        title: "Delete",
        icon: <DeleteRuleIcon />,
        id: "delete",
        onClick: () => {
          onDelete();
          setShowMenu(false);
        },
      } as RuleEditorSubmenuOption);
    }
    return options.length > 0 ? options : null; // Show menu options only if not empty
  }, [isActive, onChange, onDelete, onDuplicate]);

  function renderActionValueInput() {
    if (!actionProperty) {
      return null;
    }
    const elementType = getElementTypeForId(elementId);
    const actionValueType = getPropertyTypeForElement(actionProperty);
    switch (actionValueType) {
      case ElementRuleValueType.color: {
        const options = getPropertyOptions(elementType, actionProperty);
        return (
          <>
            <span>to</span>
            <div className={style.color} style={{ userSelect: isActive ? "inherit" : "none" }}>
              <ColorPicker
                colorPalette={options}
                onChange={setActionValue}
                value={actionValue}
                theme="dark"
                boundary="stage-container"
                allowCustomColor={elementType === consts.CANVAS_ELEMENTS.SHAPE}
                customColors={[]}
                tooltip=""
                portalId={PortalID.Floaters}
                editable={isActive}
              />
            </div>
          </>
        );
      }
      case ElementRuleValueType.text: {
        return (
          <>
            <span>to</span>
            <RuleEditableText
              value={actionValue}
              placeholder="Value"
              name="property-value"
              onChange={setActionValue}
              editable={isActive}
            />
          </>
        );
      }
      case ElementRuleValueType.icon: {
        return (
          <>
            <span>to</span>
            <RuleIconPicker value={actionValue} onChange={setActionValue} editable={isActive} />
          </>
        );
      }
      default: {
        return null;
      }
    }
  }

  function renderComparator() {
    if (fieldId && fieldId.startsWith("integration:")) {
      const field = getField(fieldId);
      const labels = JSON.parse(field?.settings_str ?? "{}").labels as Record<string, string> | undefined;

      if (labels) {
        return (
          <div className={style.ruleSegment}>
            <span>is</span>
            <RuleEditableDropdown
              value={value}
              placeholder="Value"
              name="value"
              options={Object.values(labels)}
              getTitle={(label) => label}
              onChange={(label) => {
                setComparator(ElementRuleComparator.eq);
                setValue(label);
              }}
              editable={isActive}
            />
          </div>
        );
      }
    }

    return (
      <>
        <div className={style.ruleSegment}>
          {shouldRenderIs(comparator) && <span>is</span>}
          <RuleEditableDropdown
            value={comparator}
            placeholder="Condition"
            name="condition"
            options={Object.values(ElementRuleComparator)}
            getTitle={getComparatorTitle}
            onChange={setComparator}
            editable={isActive}
          />
        </div>
        <div className={style.ruleSegment}>
          {shouldRenderTo(comparator) && <span>to</span>}
          <RuleEditableText value={value} placeholder="Value" name="value" onChange={setValue} editable={isActive} />
        </div>
      </>
    );
  }

  return (
    <div className={style.wrapper} data-is-placeholder={!!isPlaceholder}>
      <div className={classNames(style.container, { [style.inactive]: rule && !rule.active })}>
        <div className={classNames(style.ruleContainer)}>
          <div className={style.ruleSegment}>
            <span>If</span>
            <RuleCategorizedDropdown
              value={fieldId}
              valueMissing={missingField}
              name="field"
              placeholder="Field"
              options={fieldOptions}
              categories={fieldCategories}
              getTitle={getFieldTitle}
              onChange={(newFieldId) => {
                setFieldId(newFieldId);
                if (newFieldId.startsWith("integration:")) {
                  // Remove :columnId from the newFieldId. (take all subsegments except the last one)
                  setItemId(newFieldId.split(":").slice(0, -1).join(":"));
                } else {
                  setItemId("customFields");
                }
                if (rule?.fieldId !== newFieldId) {
                  setValue("");
                }
              }}
              editable={isActive}
            />
          </div>
          {renderComparator()}
          <div className={style.ruleSegment}>
            <span>change</span>
            <RuleEditableDropdown
              value={actionProperty}
              options={actionProperties}
              placeholder="Property"
              name="property"
              getTitle={getActionPropertyTitle}
              onChange={(property) => {
                setActionProperty(property);
                setActionValue(rule?.action.value);
              }}
              editable={isActive}
            />
            {renderActionValueInput()}
          </div>
        </div>
        {missingField && (
          <div className={style.missingField}>
            <MissingDataIcon />
            <span>Missing column</span>
          </div>
        )}
      </div>
      {menuOptions && (
        <div
          ref={menuRef}
          className={classNames(style.menuButton, { [style.menuButtonActive]: showMenu })}
          onClick={() => {
            setShowMenu(!showMenu);
          }}
        >
          <ThreeDotsIcon width={14} height={"100%"} />
        </div>
      )}
      {showMenu && menuOptions && (
        <Submenu relativeTo={menuRef} options={menuOptions} closeMenu={() => setShowMenu(false)} />
      )}
    </div>
  );
}

function Submenu({
  relativeTo,
  options,
  closeMenu,
}: {
  relativeTo: React.RefObject<HTMLElement>;
  options: RuleEditorSubmenuOption[];
  closeMenu: () => void;
}) {
  const ref = useRef(null);
  useOnClickOutside(ref, closeMenu, "mouseup");
  const portal = document.getElementById(PortalID.Floaters);

  const element = relativeTo.current ? (
    <Floater
      ref={ref}
      relativeTo={relativeTo}
      side={"right"}
      margin={16}
      arrowSize={12}
      arrowColor="#23405e"
      boundary={"no-controls-area"}
      extraStyle={{ marginTop: "-3px", zIndex: 11 }}
    >
      <div className={style.ruleSubmenu}>
        {options.map((option, index) => (
          <SubmenuOption key={index} option={option} />
        ))}
      </div>
    </Floater>
  ) : null;

  return portal ? ReactDOM.createPortal(element, portal) : element;
}

function SubmenuOption({ option }: { option: RuleEditorSubmenuOption }) {
  return (
    <div
      className={style.submenuOption}
      data-testid={`rule-cell-submenu-${option.id}`}
      onClick={option.onClick}
      data-id={option.id}
    >
      <div className={style.optionIcon}>{option.icon}</div>
      <span className={style.optionTitle}>{option.title}</span>
    </div>
  );
}
